import React from 'react';
import {
    Card,
    CardHeader,
    Divider,
    Table,
    TableBody,
    TextField,
    makeStyles,
    Theme,
    Fade,
    Box,
    IconButton,
    SvgIcon,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { CapitalizeNames } from 'src/utils/capitalize';
import type { IServiceClient } from 'src/interfaces/serviceClient';
import { formatDate } from 'src/utils/formatDate';
import { TextRow } from 'src/components/Lead/Information/components/TextRow';
import type { TValue } from 'src/components/Lead/Information/components/types';
import { TableRowWrapper } from 'src/components/Lead/Information/components/LeadInfo/components/TableRowWrapper';
import { AreaCodeTextField } from 'src/modules/forms/fields/AreaCodeTextField/AreaCodeTextField';
import { ECountryCode } from 'src/modules/forms/fields/AreaCodeTextField/components/AreaCodeSelect';
import { Link as RouterLink } from 'react-router-dom';
import { Edit2 } from 'react-feather';
import { useServiceClient } from 'src/hooks/useServiceClient';

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        overflow: 'scroll',
    },
    margin: {
        marginBottom: '1em',
    },
    editButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    input: {
        '& .MuiInputBase-input': {
            color: theme.palette.text.secondary,
            fontSize: '16px',
        },
        '& .MuiInput-underline:before': {
            borderBottom: 'none',
        },
        '& .MuiInput-underline:hover:before': {
            borderBottom: 'none',
        },
        '& .MuiInput-underline:after': {
            borderBottom: 'none',
        },
    },
}));

type TClientDetailFormValues = {
    name: string;
    email: string;
    phone: string;
    countryCode: ECountryCode;
};

type ClientDetailCardProps = {
    client: IServiceClient | null;
    editPath?: string;
};

export const ClientDetailCard = ({ client, editPath }: ClientDetailCardProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const loading = false;
    const { updateServiceClient } = useServiceClient();

    const createdAtRow: TValue & { dataTestId: string } = {
        header: t('serviceClients.createdAt'),
        text: formatDate(client?.createdAt) ?? '—',
        dataTestId: 'client-detail-row-createdAt',
    };

    const handleSave = async (values: TClientDetailFormValues) => {
        if (!client?._id) return;
        await updateServiceClient(client._id, values, { playSound: false });
    };

    return (
        <Fade in={true} timeout={600}>
            <Card
                className={classes.paper}
                data-testid="client-detail-card"
                elevation={0}
            >
                <Box className={classes.editButton}>
                    <CardHeader title={String(t('serviceClients.Detail'))} />
                    {editPath && (
                        <IconButton
                            component={RouterLink}
                            to={editPath}
                            size="small"
                            aria-label={t('serviceClient.UpdateServiceClient')}
                            data-testid="client-detail-card-edit-button"
                        >
                            <SvgIcon fontSize="small">
                                <Edit2 />
                            </SvgIcon>
                        </IconButton>
                    )}
                </Box>
                <Divider />
                <Formik
                    initialValues={{
                        name: client?.name ?? '',
                        email: client?.email ?? '',
                        phone: client?.phone ?? '',
                        countryCode: client?.countryCode ?? ECountryCode.Mexico,
                    }}
                    enableReinitialize
                    onSubmit={handleSave}
                >
                    {({
                        values,
                        handleChange,
                        handleBlur,
                        touched,
                        errors,
                        handleSubmit,
                        setFieldValue,
                    }) => (
                        <Table className={classes.margin}>
                            <TableBody>
                                <TableRowWrapper
                                    header={String(t('serviceClients.name'))}
                                    loading={loading}
                                    handleSubmit={handleSubmit}
                                    data-testid="client-detail-row-name"
                                    compact
                                >
                                    <TextField
                                        disabled={loading}
                                        value={CapitalizeNames(
                                            values.name || ''
                                        )}
                                        error={Boolean(
                                            touched.name && errors.name
                                        )}
                                        fullWidth
                                        helperText={touched.name && errors.name}
                                        autoComplete="off"
                                        name="name"
                                        onChange={(e) => {
                                            handleChange(e);
                                            handleBlur(e);
                                        }}
                                        className={classes.input}
                                        inputProps={{
                                            'data-testid':
                                                'client-detail-input-name',
                                        }}
                                    />
                                </TableRowWrapper>
                                <TableRowWrapper
                                    header={String(t('serviceClients.email'))}
                                    loading={loading}
                                    handleSubmit={handleSubmit}
                                    data-testid="client-detail-row-email"
                                    compact
                                >
                                    <TextField
                                        disabled={loading}
                                        value={values.email || ''}
                                        error={Boolean(
                                            touched.email && errors.email
                                        )}
                                        fullWidth
                                        helperText={
                                            touched.email && errors.email
                                        }
                                        autoComplete="off"
                                        name="email"
                                        onChange={(e) => {
                                            handleChange(e);
                                            handleBlur(e);
                                        }}
                                        className={classes.input}
                                        inputProps={{
                                            'data-testid':
                                                'client-detail-input-email',
                                        }}
                                    />
                                </TableRowWrapper>
                                <TableRowWrapper
                                    header={String(t('serviceClients.phone'))}
                                    loading={loading}
                                    handleSubmit={handleSubmit}
                                    data-testid="client-detail-row-phone"
                                    compact
                                >
                                    <AreaCodeTextField
                                        name="phone"
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        touched={touched}
                                        errors={errors}
                                        handleChange={handleChange}
                                        props={{
                                            className: classes.input,
                                            inputProps: {
                                                'data-testid':
                                                    'client-detail-input-phone',
                                                'aria-label': 'phone',
                                            },
                                        }}
                                    />
                                </TableRowWrapper>
                                <TextRow
                                    item={createdAtRow}
                                    loading={false}
                                    data-testid={createdAtRow.dataTestId}
                                />
                            </TableBody>
                        </Table>
                    )}
                </Formik>
            </Card>
        </Fade>
    );
};
